import React, { useCallback, useEffect, useMemo, useState } from "react";

import qs from "query-string";
import dayjs from "dayjs";
import Select from "react-select";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { useLocation, useHistory } from "react-router-dom";
import { useMutation, useQuery } from "@apollo/client";
import { useDebounce } from "react-use";

import Modal from "react-bootstrap/Modal";
import Button from "react-bootstrap/Button";
import useToggle from "react-use/lib/useToggle";

import Table from "../../../components/Table";
import Pagination from "../../../components/Pagination";

import {
  QUERY_SYNGUARD_STUDENTS,
  QUERY_ENTITIES,
} from "../../../config/graphql/query";
import {
  MUTATION_RESET_SYNGUARD_STUDENT,
  MUTATION_RESET_SYNGUARD_STUDENTS,
  MUTATION_BLOCK_SYNGUARD_STUDENT,
  MUTATION_BLOCK_SYNGUARD_STUDENTS,
  MUTATION_DELETE_SYNGUARD_STUDENT,
  MUTATION_DELETE_SYNGUARD_STUDENTS,
} from "../../../config/graphql/mutation";
import { reactSelectCustomStyles } from "../../Employees/Employee/Information";

const limit = 10;

const StudentTableRow = ({
  item,
  variables,
  index,
  checked,
  onCheck,
}: {
  item: ISynguardStudent;
  variables: any;
  index: number;
  checked: boolean;
  onCheck: (event: React.ChangeEvent<HTMLInputElement>) => void;
}) => {
  const { t } = useTranslation(["client", "common"]);
  const [showDelete, setShowDelete] = useToggle(false);

  const [onReset] = useMutation(MUTATION_RESET_SYNGUARD_STUDENT, {
    refetchQueries: [{ query: QUERY_SYNGUARD_STUDENTS, variables }],
    variables: { id: item.id },
  });

  const [onBlock] = useMutation(MUTATION_BLOCK_SYNGUARD_STUDENT, {
    refetchQueries: [{ query: QUERY_SYNGUARD_STUDENTS, variables }],
    variables: { id: item.id },
  });

  const [onDelete] = useMutation(MUTATION_DELETE_SYNGUARD_STUDENT, {
    refetchQueries: [{ query: QUERY_SYNGUARD_STUDENTS, variables }],
    variables: { id: item.id },
  });

  const onBeforeUnblock = () => {
    onReset()
      .then(() =>
        toast.success<string>(
          t("client:client.synguard.students.toast.unblocked"),
        ),
      )
      .catch(() =>
        toast.error<string>(
          t("client:client.synguard.students.toast.unblockedError"),
        ),
      );
  };

  const onBeforeBlock = () => {
    onBlock()
      .then(() =>
        toast.success<string>(
          t("client:client.synguard.students.toast.blocked"),
        ),
      )
      .catch(() =>
        toast.error<string>(
          t("client:client.synguard.students.toast.blockedError"),
        ),
      );
  };

  const onBeforeDelete = () => {
    setShowDelete(false);
    onDelete()
      .then(() =>
        toast.success<string>(
          t("client:client.synguard.students.toast.deleted"),
        ),
      )
      .catch(() =>
        toast.error<string>(
          t("client:client.synguard.students.toast.deleteError"),
        ),
      );
  };

  return (
    <>
      <tr>
        <td className="text-center">
          <input
            value={item.id}
            checked={checked}
            type="checkbox"
            onChange={onCheck}
          />
        </td>
        <td className="text-center">{index + 1}</td>
        <td>{item.email}</td>
        <td>{item.entity?.title || "—"}</td>
        <td>{item.accessCount}</td>
        <td
          className={`${item.blocked ? "bg-danger" : "bg-success"} text-white`}
        >
          {item.blocked
            ? t("client:client.synguard.students.status.blocked")
            : t("client:client.synguard.students.status.active")}
        </td>
        <td>{dayjs(item.updatedAt).format("DD.MM.YYYY HH:mm")}</td>
        <td className="text-nowrap">
          <div className="d-flex">
            {item.blocked ? (
              <Button
                size="sm"
                variant="success"
                className="mr-2"
                onClick={onBeforeUnblock}
              >
                {t("client:client.synguard.students.table.buttons.unblock")}
              </Button>
            ) : (
              <Button
                size="sm"
                variant="secondary"
                className="mr-2"
                onClick={onBeforeBlock}
              >
                {t("client:client.synguard.students.table.buttons.block")}
              </Button>
            )}
            <Button
              size="sm"
              variant="danger"
              onClick={() => setShowDelete(true)}
            >
              {t("common:delete")}
            </Button>
          </div>
        </td>
      </tr>
      <Modal size="sm" show={showDelete} onHide={setShowDelete} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            {t("client:client.synguard.students.modalDelete.title")}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {t("client:client.synguard.students.modalDelete.body", {
            email: item.email,
          })}
        </Modal.Body>
        <Modal.Footer>
          <Button size="sm" onClick={() => setShowDelete(false)}>
            {t("common:cancel")}
          </Button>
          <Button size="sm" variant="danger" onClick={onBeforeDelete}>
            {t("common:delete")}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

const Students = () => {
  const location = useLocation();
  const history = useHistory();

  const { t } = useTranslation(["client", "common", "visitor", "accessEvent"]);

  const query = useMemo(
    () =>
      qs.parse(location.search, { parseNumbers: true }) as {
        page?: number;
        search?: string;
      },
    [location.search],
  );

  const [search, setSearch] = useState(query.search);
  const [entityId, setEntityId] = useState<string | undefined>();
  const [modal, setModal] = useToggle(false);
  const [bulkBlockModal, setBulkBlockModal] = useToggle(false);
  const [checked, setChecked] = useState<string[]>([]);

  const page = useMemo(() => Math.max(query.page || 0, 1), [query.page]);

  const variables = useMemo(() => {
    const filter: any = {};

    if (query.search) {
      filter.email = { REGEX: query.search, OPTIONS: "i" };
    }

    if (entityId) {
      filter.entityId = entityId;
    }

    return {
      filter,
      sort: { createdAt: "DESC" },
      pagination: { limit, skip: (page - 1) * limit },
    };
  }, [page, query.search, entityId]);

  const { data, loading } = useQuery<{
    synguardStudents: ISynguardStudent[];
    synguardStudentsCount: number;
  }>(QUERY_SYNGUARD_STUDENTS, {
    variables,
    fetchPolicy: "network-only",
  });

  const students = useMemo(
    () => data?.synguardStudents ?? [],
    [data?.synguardStudents],
  );

  const studentsCount = useMemo(
    () => data?.synguardStudentsCount ?? 0,
    [data?.synguardStudentsCount],
  );

  const { data: entityListData } = useQuery<{
    entities: { id: string; title: string }[];
  }>(QUERY_ENTITIES);

  const entityOptions = useMemo(
    () =>
      entityListData?.entities.map((entity) => ({
        value: entity.id,
        label: entity.title,
      })) ?? [],
    [entityListData],
  );

  const onCheck = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      let updatedList = [...checked];
      if (event.target.checked) {
        updatedList = [...checked, event.target.value];
      } else {
        updatedList.splice(checked.indexOf(event.target.value), 1);
      }
      setChecked(updatedList);
    },
    [checked],
  );

  const onAllCheck = useCallback(() => {
    setChecked(students.map((s) => s.id));
  }, [students]);

  const onAllUncheck = useCallback(() => {
    setChecked([]);
  }, []);

  useEffect(() => {
    setChecked([]);
  }, [page]);

  useDebounce(
    () => {
      if (search !== undefined) {
        history.push({
          search: qs.stringify({ page: 1, search }),
        });
      }
    },
    500,
    [search],
  );

  const [onBulkReset] = useMutation(MUTATION_RESET_SYNGUARD_STUDENTS, {
    refetchQueries: [{ query: QUERY_SYNGUARD_STUDENTS, variables }],
  });

  const [onBulkBlock] = useMutation(MUTATION_BLOCK_SYNGUARD_STUDENTS, {
    refetchQueries: [{ query: QUERY_SYNGUARD_STUDENTS, variables }],
  });

  const [onBulkDelete] = useMutation(MUTATION_DELETE_SYNGUARD_STUDENTS, {
    refetchQueries: [{ query: QUERY_SYNGUARD_STUDENTS, variables }],
  });

  const onBulkUnblockAction = () => {
    onBulkReset({ variables: { ids: checked } })
      .then(() => {
        toast.success<string>(
          t("client:client.synguard.students.toast.bulkUnblocked"),
        );
        setChecked([]);
      })
      .catch(() =>
        toast.error<string>(
          t("client:client.synguard.students.toast.unblockedError"),
        ),
      );
  };

  const onBulkBlockAction = () => {
    setBulkBlockModal(false);
    onBulkBlock({ variables: { ids: checked } })
      .then(() => {
        toast.success<string>(
          t("client:client.synguard.students.toast.bulkBlocked"),
        );
        setChecked([]);
      })
      .catch(() =>
        toast.error<string>(
          t("client:client.synguard.students.toast.blockedError"),
        ),
      );
  };

  const onBulkDeleteAction = () => {
    setModal(false);
    onBulkDelete({ variables: { ids: checked } })
      .then(() => {
        toast.success<string>(
          t("client:client.synguard.students.toast.bulkDeleted"),
        );
        setChecked([]);
      })
      .catch(() =>
        toast.error<string>(
          t("client:client.synguard.students.toast.deleteError"),
        ),
      );
  };

  const renderStudent = useCallback(
    (item: ISynguardStudent, index: number) => (
      <StudentTableRow
        key={item.id}
        item={item}
        variables={variables}
        index={(page - 1) * limit + index}
        checked={checked.includes(item.id)}
        onCheck={onCheck}
      />
    ),
    [variables, page, checked, onCheck],
  );

  return (
    <div className="container-fluid">
      <nav aria-label="breadcrumb">
        <ol className="breadcrumb my-3">
          <li className="breadcrumb-item active" aria-current="page">
            {t("client:client.synguard.students.title")}
          </li>
        </ol>
      </nav>
      <div className="d-flex flex-wrap justify-content-between mt-4">
        <div>
          <Button className="mr-2 mb-4" onClick={onAllCheck}>
            {t("visitor:visitors.button.selectAll")}
          </Button>
          <Button className="mr-2 mb-4" onClick={onAllUncheck}>
            {t("visitor:visitors.button.unselectAll")}
          </Button>
          <Button
            className="mr-2 mb-4"
            variant="success"
            disabled={!checked.length}
            onClick={onBulkUnblockAction}
          >
            {t("client:client.synguard.students.table.buttons.unblockSelected")}
          </Button>
          <Button
            className="mr-2 mb-4"
            variant="secondary"
            disabled={!checked.length}
            onClick={() => setBulkBlockModal(true)}
          >
            {t("client:client.synguard.students.table.buttons.blockSelected")}
          </Button>
          <Button
            className="mr-2 mb-4"
            variant="danger"
            disabled={!checked.length}
            onClick={() => setModal(true)}
          >
            {t("client:client.synguard.students.table.buttons.deleteSelected")}
          </Button>
        </div>
        <div className="d-flex flex-wrap">
          <form className="form-inline mr-2">
            <div className="mr-5 mb-4">
              <label htmlFor="search" className="sr-only">
                {t("common:search")}
              </label>
              <input
                id="search"
                type="search"
                className="form-control"
                placeholder={t("client:client.synguard.students.search")}
                value={search ?? ""}
                onChange={({ target: { value } }) => setSearch(value)}
              />
            </div>
          </form>
          <div className="d-flex mb-4">
            <label htmlFor="location" className="mb-0 mr-2 align-self-center">
              {t("accessEvent:accessEvents.location.label")}
            </label>
            <div style={{ width: "12rem" }}>
              <Select
                isClearable
                options={entityOptions}
                value={
                  entityOptions.find((opt) => opt.value === entityId) || null
                }
                onChange={(value) => setEntityId(value?.value)}
                styles={reactSelectCustomStyles(false)}
              />
            </div>
          </div>
        </div>
      </div>

      <Table striped bordered hover responsive>
        <thead>
          <tr>
            <th scope="col" style={{ width: "3.5rem" }} />
            <th scope="col" style={{ width: "3.5rem", textAlign: "center" }}>
              #
            </th>
            <th scope="col">
              {t("client:client.synguard.students.table.th.email")}
            </th>
            <th scope="col">{t("accessEvent:accessEvents.th.location")}</th>
            <th scope="col">
              {t("client:client.synguard.students.table.th.accessCount")}
            </th>
            <th scope="col">
              {t("client:client.synguard.students.table.th.status")}
            </th>
            <th scope="col">
              {t("client:client.synguard.students.table.th.lastAccess")}
            </th>
            <th scope="col">
              {t("client:client.synguard.students.table.th.actions")}
            </th>
          </tr>
        </thead>
        <tbody>
          {students.map(renderStudent)}
          {!loading && students.length === 0 && (
            <tr>
              <td colSpan={8} className="text-center">
                {t("client:client.synguard.students.table.noStudents")}
              </td>
            </tr>
          )}
        </tbody>
      </Table>

      <Pagination documentsCount={studentsCount} limit={limit} />

      <Modal size="sm" show={modal} onHide={setModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            {t("client:client.synguard.students.modalDelete.title")}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {t("client:client.synguard.students.modalDelete.bulkBody", {
            count: checked.length,
          })}
        </Modal.Body>
        <Modal.Footer>
          <Button size="sm" onClick={() => setModal(false)}>
            {t("common:cancel")}
          </Button>
          <Button size="sm" variant="danger" onClick={onBulkDeleteAction}>
            {t("common:delete")}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        size="lg"
        show={bulkBlockModal}
        onHide={setBulkBlockModal}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {t("client:client.synguard.students.modalBlock.title")}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {t("client:client.synguard.students.modalBlock.bulkBody", {
            count: checked.length,
          })}
        </Modal.Body>
        <Modal.Footer>
          <Button size="sm" onClick={() => setBulkBlockModal(false)}>
            {t("common:cancel")}
          </Button>
          <Button size="sm" variant="secondary" onClick={onBulkBlockAction}>
            {t("client:client.synguard.students.modalBlock.confirm")}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default React.memo(Students);
